/**
 * Luxury product video — hover-to-play desktop, autoplay muted mobile, lazy loading.
 */

(function () {
  const getDesktopHover = () => window.matchMedia('(min-width: 750px) and (hover: hover)').matches;
  const getMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const getReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setLoading(container, isLoading) {
    container.classList.toggle('is-loading', isLoading);
    if (isLoading) {
      container.setAttribute('aria-busy', 'true');
    } else {
      container.removeAttribute('aria-busy');
    }
  }

  function loadNativeVideo(video) {
    if (!video || video.dataset.luxuryLoaded === 'true') {
      return Promise.resolve(video);
    }

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    video.dataset.luxuryLoaded = 'true';
    video.load();
    return Promise.resolve(video);
  }

  function loadExternalVideo(container) {
    if (container.dataset.luxuryLoaded === 'true') return;

    const template = container.querySelector('template');
    if (!template?.content?.firstElementChild) return;

    const iframe = template.content.firstElementChild.cloneNode(true);
    container.appendChild(iframe);
    container.dataset.luxuryLoaded = 'true';
  }

  async function playVideoElement(video) {
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    try {
      await video.play();
    } catch (_error) {
      /* Autoplay may be blocked until user interaction */
    }
  }

  function pauseVideoElement(video) {
    if (!video) return;
    video.pause();
  }

  function pauseExternal(container) {
    container.querySelectorAll('.js-youtube, .js-vimeo').forEach((iframe) => {
      if (iframe.classList.contains('js-youtube')) {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (iframe.classList.contains('js-vimeo')) {
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
      }
    });
  }

  async function playCard(container) {
    if (getReducedMotion()) return;

    const mediaWrap = container.closest('.product-card__media');
    setLoading(container, true);

    const type = container.dataset.videoType;
    let didStart = false;

    if (type === 'native') {
      const video = container.querySelector('video');
      await loadNativeVideo(video);
      try {
        await playVideoElement(video);
        didStart = !video.paused;
      } catch (_error) {
        didStart = false;
      }
    } else if (type === 'external') {
      loadExternalVideo(container);
      didStart = Boolean(container.querySelector('.js-youtube, .js-vimeo'));
    }

    if (didStart) {
      container.classList.add('is-playing');
      mediaWrap?.classList.add('is-video-playing');
    } else {
      container.classList.remove('is-playing');
      mediaWrap?.classList.remove('is-video-playing');
    }

    setLoading(container, false);
  }

  function pauseCard(container) {
    container.classList.remove('is-playing');
    container.closest('.product-card__media')?.classList.remove('is-video-playing');

    const video = container.querySelector('video');
    if (video) pauseVideoElement(video);
    pauseExternal(container);
  }

  function initCardVideos(root = document) {
    root.querySelectorAll('.luxury-video--card[data-luxury-video]').forEach((container) => {
      if (container.dataset.luxuryInit === 'true') return;
      container.dataset.luxuryInit = 'true';

      if (getDesktopHover()) {
        container.addEventListener('mouseenter', () => playCard(container));
        container.addEventListener('mouseleave', () => pauseCard(container));
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
              playCard(container);
            } else {
              pauseCard(container);
            }
          });
        },
        { threshold: [0, 0.45, 0.75] }
      );

      observer.observe(container);
    });
  }

  async function playGalleryDeferred(deferredMedia) {
    if (!deferredMedia || getReducedMotion()) return;

    setLoading(deferredMedia, true);
    deferredMedia.classList.add('is-playing');

    if (!deferredMedia.hasAttribute('loaded')) {
      deferredMedia.loadContent(false);
    }

    const video = deferredMedia.querySelector('video');
    if (video) {
      await playVideoElement(video);
    }

    setLoading(deferredMedia, false);
  }

  function pauseGalleryDeferred(deferredMedia) {
    if (!deferredMedia) return;

    deferredMedia.classList.remove('is-playing', 'is-loading');
    deferredMedia.removeAttribute('aria-busy');

    const video = deferredMedia.querySelector('video');
    if (video) pauseVideoElement(video);
  }

  function initGalleryVideos(root = document) {
    const gallery = root.querySelector('.product-gallery--luxury');
    if (!gallery) return;

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;

    const syncActiveSlide = () => {
      viewer.querySelectorAll('.product__media-item').forEach((slide) => {
        const deferredMedia = slide.querySelector('deferred-media.luxury-video--gallery');
        if (!deferredMedia) return;

        if (slide.classList.contains('is-active')) {
          if (getMobile()) {
            playGalleryDeferred(deferredMedia);
          }
        } else {
          pauseGalleryDeferred(deferredMedia);
        }
      });
    };

    viewer.querySelectorAll('.product__media-item').forEach((slide) => {
      const deferredMedia = slide.querySelector('deferred-media.luxury-video--gallery');
      if (!deferredMedia) return;

      slide.addEventListener('mouseenter', () => {
        if (!getDesktopHover() || !slide.classList.contains('is-active')) return;
        playGalleryDeferred(deferredMedia);
      });

      slide.addEventListener('mouseleave', () => {
        if (!getDesktopHover() || !slide.classList.contains('is-active')) return;
        pauseGalleryDeferred(deferredMedia);
      });
    });

    const observer = new MutationObserver(syncActiveSlide);
    viewer.querySelectorAll('.product__media-item').forEach((slide) => {
      observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });

    syncActiveSlide();
  }

  function init(root = document) {
    initCardVideos(root);
    initGalleryVideos(root);
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.LuxuryVideo = { init, playGalleryDeferred, pauseGalleryDeferred };
})();
